import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiAlertCircle } from 'react-icons/fi';
import {
  getPlanTagline,
  getPlanBulletPoints,
  getPlanSignupBulletPoints,
  formatPlanPrice,
  getPlanBillingPeriodLabel,
  isPlanPopular,
} from '../../utils/planUtils';
import PlanBillingCycleToggle from './PlanBillingCycleToggle';

function planIdOf(plan) {
  if (!plan) return null;
  return plan._id || plan.id || null;
}

const PlanSelectionCards = ({
  plans,
  selectedPlan,
  onSelect,
  loading,
  onRetry,
  modoOscuro,
  layout = 'grid',
  compact = false,
  signup = false,
  billingCycle = 'monthly',
  onBillingCycleChange,
  betaSignupZeroPrice = false,
  betaFreeDays,
  textPri,
  textSec,
  textMuted,
}) => {
  if (loading) {
    return (
      <div className="text-center py-10">
        <div
          className={`animate-spin rounded-full h-10 w-10 border-2 mx-auto mb-3 ${
            modoOscuro ? 'border-gray-600 border-t-gray-300' : 'border-gray-300 border-t-gray-900'
          }`}
        />
        <p className={`text-sm ${textSec}`}>Cargando planes...</p>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="text-center py-10">
        <FiAlertCircle className={`w-8 h-8 mx-auto mb-3 ${textSec}`} />
        <p className={`text-sm mb-3 ${textSec}`}>No se pudieron cargar los planes.</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="px-4 py-2 rounded-full bg-[#1F80FF] text-white text-sm font-semibold hover:bg-[#004AAD] transition-colors"
          >
            Reintentar
          </button>
        )}
      </div>
    );
  }

  const gridClass = signup
    ? 'grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-stretch'
    : layout === 'list'
      ? 'grid grid-cols-1 gap-3'
      : 'grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 items-stretch';

  const resolveBullets = (plan) =>
    compact ? getPlanSignupBulletPoints(plan) : getPlanBulletPoints(plan);

  const cycle = billingCycle === 'annual' ? 'annual' : 'monthly';
  const periodLabel = getPlanBillingPeriodLabel(cycle);
  const betaHint =
    signup && betaSignupZeroPrice
      ? `Hoy $0 en beta${betaFreeDays ? ` (${betaFreeDays} días)` : ''}. Después se cobra el plan en ciclo ${
          cycle === 'annual' ? 'anual' : 'mensual'
        } que elijas.`
      : undefined;

  return (
    <div className="space-y-4">
      {signup && onBillingCycleChange && (
        <PlanBillingCycleToggle
          billingCycle={cycle}
          onChange={onBillingCycleChange}
          modoOscuro={modoOscuro}
          textMuted={textMuted}
          hint={betaHint}
        />
      )}
      <div className={gridClass}>
        {plans.map((plan) => {
          const pid = planIdOf(plan);
          const isSelected = planIdOf(selectedPlan) === pid;
          const popular = isPlanPopular(plan);

          return (
            <button
              key={pid || plan.name}
              type="button"
              onClick={() => onSelect(plan)}
              className={`relative flex h-full min-h-0 flex-col rounded-2xl border p-4 sm:p-5 text-left transition-all duration-200 ${
                popular ? 'pt-7 sm:pt-8' : ''
              } ${
                isSelected
                  ? 'border-[#1F80FF] shadow-lg ring-1 ring-[#1F80FF]/20'
                  : modoOscuro
                    ? 'border-gray-700 hover:border-gray-600 bg-gray-900'
                    : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
              } ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}
            >
              {popular && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 sm:left-5 sm:translate-x-0 rounded-full bg-[#1F80FF] px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm whitespace-nowrap">
                  Más popular
                </div>
              )}

              <div className="mb-2 flex min-h-[2.5rem] items-start justify-between gap-2">
                <h4 className={`text-base font-bold leading-tight pr-2 ${textPri}`}>{plan.name}</h4>
                {isSelected && (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1F80FF]">
                    <FiCheck className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>

              <p className={`mb-3 text-xs sm:text-sm leading-snug line-clamp-3 ${textSec}`}>
                {getPlanTagline(plan)}
              </p>

              <div className="mb-3">
                <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                  <span className={`text-xl sm:text-2xl font-bold ${textPri}`}>
                    {formatPlanPrice(plan, cycle)}
                  </span>
                  <span className={`text-xs sm:text-sm ${textMuted}`}>
                    /{periodLabel} ARS
                    {cycle === 'annual' ? ' · 2 meses bonificados' : ''}
                  </span>
                </div>
                {signup && betaSignupZeroPrice && (
                  <p
                    className={`mt-1 text-sm font-semibold ${
                      modoOscuro ? 'text-emerald-400' : 'text-emerald-700'
                    }`}
                  >
                    En beta: $0
                  </p>
                )}
              </div>

              <ul className="mt-auto space-y-1.5 text-xs sm:text-sm">
                {resolveBullets(plan).map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <FiCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 text-[#1F80FF] shrink-0" />
                    <span className={`leading-snug ${textSec}`}>{item}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <p className={`text-center text-xs leading-relaxed px-2 ${textMuted}`}>
        Precios en pesos argentinos.{' '}
        <Link to="/terminos" className="text-[#1F80FF] hover:underline">
          Términos
        </Link>
        {' · '}
        <Link to="/privacidad" className="text-[#1F80FF] hover:underline">
          Privacidad
        </Link>
        . ¿Comparar en detalle?{' '}
        <Link to="/planes" className="font-medium text-[#1F80FF] hover:underline underline-offset-2">
          Ver todos los planes
        </Link>
      </p>
    </div>
  );
};

export default PlanSelectionCards;
